import { PDFDocument, rgb, StandardFonts } from 'pdf-lib';

// Configuration for BIR Form 2307 Fields
// Coordinates are in points (1/72 inch). Origin (0,0) is usually BOTTOM-LEFT of the page.
// You may need to adjust these values based on your specific PDF template.
const FORM_2307_CONFIG = {
  // Header Dates (Period From/To) - Approx Top Right
  periodFrom: { x: 340, y: 670, size: 10 },
  periodTo: { x: 450, y: 670, size: 10 },

  // Part I - Payee Information
  payeeTin: { x: 130, y: 635, size: 10 },
  payeeName: { x: 130, y: 615, size: 10 },
  payeeAddress: { x: 130, y: 595, size: 9 },
  payeeZip: { x: 500, y: 595, size: 10 },

  // Part II - Payor Information
  payorTin: { x: 130, y: 560, size: 10 },
  payorName: { x: 130, y: 540, size: 10 },
  payorAddress: { x: 130, y: 520, size: 9 },
  payorZip: { x: 500, y: 520, size: 10 },

  // Part III - Payments Table
  // Starting Y position for the first row
  tableStartY: 460,
  rowHeight: 15, // Space between rows
  colNature: { x: 30 },
  colAtc: { x: 250 },
  colAmount: { x: 350 }, // Right aligned usually, but text is drawn left-aligned by default unless calculated
  colTax: { x: 480 },

  // Totals
  totalAmount: { x: 350, y: 150, size: 10 },
  totalTax: { x: 480, y: 150, size: 10 },
};

const FORM_2306_CONFIG = {
  periodFrom: { x: 340, y: 670, size: 10 },
  periodTo: { x: 450, y: 670, size: 10 },
  payeeTin: { x: 130, y: 635, size: 10 },
  payeeName: { x: 130, y: 615, size: 10 },
};

const parseColor = (color) => {
  if (!color) return rgb(0, 0, 0);
  if (typeof color === 'string') {
    let hex = color.trim();
    if (hex.startsWith('#')) hex = hex.slice(1);
    if (hex.length === 3) {
      hex = hex
        .split('')
        .map((c) => c + c)
        .join('');
    }
    if (hex.length !== 6) return rgb(0, 0, 0);
    const r = parseInt(hex.slice(0, 2), 16) / 255;
    const g = parseInt(hex.slice(2, 4), 16) / 255;
    const b = parseInt(hex.slice(4, 6), 16) / 255;
    if (Number.isNaN(r) || Number.isNaN(g) || Number.isNaN(b)) {
      return rgb(0, 0, 0);
    }
    return rgb(r, g, b);
  }
  if (typeof color === 'object' && color !== null) {
    const r = color.r ?? color.red ?? 0;
    const g = color.g ?? color.green ?? 0;
    const b = color.b ?? color.blue ?? 0;
    const cr = Math.max(0, Math.min(1, r));
    const cg = Math.max(0, Math.min(1, g));
    const cb = Math.max(0, Math.min(1, b));
    return rgb(cr, cg, cb);
  }
  return rgb(0, 0, 0);
};

export const fillBirForm = async (formType, data, mappingConfig = null, templateUrl = null) => {
  try {
    // 1. Load the template
    let existingPdfBytes;
    
    const fetchTemplate = async (path) => {
        const res = await fetch(path);
        if (!res.ok) throw new Error(`Status ${res.status}`);
        return res.arrayBuffer();
    };

    const templateName = formType === '2307' ? 'BIR_Form_2307.pdf' : 'BIR_Form_2306.pdf';

    if (templateUrl) {
      try {
        existingPdfBytes = await fetchTemplate(templateUrl);
      } catch (e) {
        console.warn(`Failed to load from URL: ${templateUrl}, falling back to default.`, e);
      }
    }

    if (!existingPdfBytes) {
      // Try absolute path first
      try {
        existingPdfBytes = await fetchTemplate(`/templates/${templateName}`);
      } catch (e1) {
        console.warn(`Failed to load from /templates/${templateName}`, e1);
        // Try relative path
        try {
            existingPdfBytes = await fetchTemplate(`templates/${templateName}`);
        } catch (e2) {
            console.error(`Failed to load from templates/${templateName}`, e2);
            throw new Error(`Could not load template ${templateName}. Ensure it exists in public/templates/`);
        }
      }
    }

    // 2. Load the PDFDocument
    const pdfDoc = await PDFDocument.load(existingPdfBytes);
    const fontRegular = await pdfDoc.embedFont(StandardFonts.Helvetica);
    const fontBold = await pdfDoc.embedFont(StandardFonts.HelveticaBold);
    const fontItalic = await pdfDoc.embedFont(StandardFonts.HelveticaOblique);
    const fontBoldItalic = await pdfDoc.embedFont(StandardFonts.HelveticaBoldOblique);

    const pages = pdfDoc.getPages();
    const firstPage = pages[0];
    const { width, height } = firstPage.getSize();
    console.log(`PDF Loaded. Page Size: ${width} x ${height}`);

    // Use mappingConfig if provided, otherwise fallback to default
    let config;
    if (mappingConfig && Object.keys(mappingConfig).length > 0) {
        config = mappingConfig;
        console.log('Using Custom Mapping Config:', config);
    } else {
        config = formType === '2307' ? FORM_2307_CONFIG : FORM_2306_CONFIG;
        console.log('Using Default Config:', config);
    }

    const drawText = (text, x, y, size = 10, color, options = {}) => {
      if (text === undefined || text === null || text === '') {
        console.log(`Skipping empty text for coord (${x}, ${y})`);
        return;
      }
      // Coordinate Validation
      if (x < 0 || y < 0 || x > width || y > height) {
         console.warn(`WARNING: Text "${text}" is being drawn at (${x}, ${y}) which is outside visible page bounds (${width}x${height})!`);
      }

      console.log(`Drawing text "${text}" at (${x}, ${y}) size ${size}`);
      
      let selectedFont = fontBold;
      const weight = options.fontWeight || 'bold';
      const style = options.fontStyle || 'normal';

      if (weight === 'bold' && style === 'italic') selectedFont = fontBoldItalic;
      else if (weight === 'bold') selectedFont = fontBold;
      else if (style === 'italic') selectedFont = fontItalic;
      else selectedFont = fontRegular;

      const drawOptions = {
        x,
        y,
        size,
        font: selectedFont,
        color: parseColor(color),
      };

      if (options.wrapText && options.width) {
          drawOptions.maxWidth = options.width;
          drawOptions.wordBreaks = [" "];
          drawOptions.lineHeight = size; // Set line height to font size for tight spacing
      }

      firstPage.drawText(String(text), drawOptions);
    };

    const asConfigArray = (maybeConfig) => {
        if (!maybeConfig) return [];
        if (Array.isArray(maybeConfig)) return maybeConfig.filter(Boolean);
        return [maybeConfig];
    };

    // Helper to get config safely with hybrid support
    const resolveConfig = (key) => {
        // 1. Priority: Custom Mapping
        if (mappingConfig && mappingConfig[key]) {
            return mappingConfig[key];
        }
        
        // 2. Fallback: Default Config (ONLY if we are using the Default PDF Template)
        // If templateUrl is present, it means we are using a custom PDF, so default coords are invalid.
        if (!templateUrl) {
            const defaultKeyMap = {
                'payee.tin': 'payeeTin',
                'payee.name': 'payeeName',
                'payee.address': 'payeeAddress',
                'payee.zip': 'payeeZip',
                'payor.tin': 'payorTin',
                'payor.name': 'payorName',
                'payor.address': 'payorAddress',
                'payor.zip': 'payorZip',
                'dateRange.startDate': 'periodFrom',
                'dateRange.endDate': 'periodTo',
                'totals.amount': 'totalAmount',
                'totals.tax': 'totalTax'
            };
            
            const defaultKey = defaultKeyMap[key];
            if (defaultKey && config[defaultKey]) {
                return config[defaultKey];
            }
        }
        
        return null;
    };

    // 3. Fill Fields using dot notation keys
    const { payee, dateRange, payments, payor } = data;

    // Helper to get nested value from data object
    const getNestedValue = (obj, path) => {
        return path.split('.').reduce((acc, part) => acc && acc[part], obj);
    };

    // Define all possible fields we want to try to draw
    const allFields = [
        'payee.tin', 'payee.name', 'payee.address', 'payee.zip',
        'payor.tin', 'payor.name', 'payor.address', 'payor.zip',
        'dateRange.startDate', 'dateRange.endDate',
        'dateRange.from', 'dateRange.to', // Explicitly add new fields
        'totals.amount', 'totals.tax',
        // Transaction Details
        'transaction.cafoa_no', 'transaction.dv_no', 'transaction.voucher_amount',
        'transaction.check_no', 'transaction.check_date', 'transaction.particulars',
        // Supplier Details
        'supplier.atc', 'supplier.nature_of_payment', 
        'supplier.atc_2306', 'supplier.nature_of_payment_2306',
        'supplier.atc_2307', 'supplier.nature_of_payment_2307',
        'supplier.tax_rate_2306', 'supplier.tax_rate_2307'
    ];

    // MERGE allFields with keys from mappingConfig to ensure we cover everything
    const mappedKeys = mappingConfig ? Object.keys(mappingConfig) : [];
    // Use Set to remove duplicates
    const fieldsToProcess = [...new Set([...allFields, ...mappedKeys])];

    if (mappingConfig && Array.isArray(mappingConfig._custom)) {
        mappingConfig._custom.forEach((customItem) => {
            const customText = (customItem?.value !== undefined && customItem?.value !== null && customItem?.value !== '')
                ? customItem.value
                : (customItem?.label || '');

            if (!customText) return;
            if (customItem?.x === undefined || customItem?.y === undefined) return;

            drawText(customText, customItem.x, customItem.y, customItem.size || 10, customItem.color, customItem);
        });
    }

    // Iterate and Draw
    fieldsToProcess.forEach(key => {
        // Skip table config keys as they are handled separately
        if (key.startsWith('table.') || key.startsWith('_')) return;

        let value;

        // Special handling for totals
        if (key === 'totals.amount') {
            const totalAmount = payments.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);
            value = totalAmount.toLocaleString('en-US', { minimumFractionDigits: 2 });
        } else if (key === 'totals.tax') {
            const totalTax = payments.reduce((sum, p) => sum + (parseFloat(p.taxWithheld) || 0), 0);
            value = totalTax.toLocaleString('en-US', { minimumFractionDigits: 2 });
        } else if (key === 'dateRange.from') {
            // Logic: Start of the month of the Voucher Date
            const voucherDateStr = data.transaction?.voucher_date;
            console.log(`[birFormFiller] Processing dateRange.from. Voucher Date: "${voucherDateStr}"`);
            
            if (voucherDateStr) {
                const date = new Date(voucherDateStr);
                if (!isNaN(date.getTime())) {
                    // Set to first day of month
                    const firstDay = new Date(date.getFullYear(), date.getMonth(), 1);
                    // Format: MM DD YYYY (e.g. 10 01 2025)
                    const mm = String(firstDay.getMonth() + 1).padStart(2, '0');
                    const dd = String(firstDay.getDate()).padStart(2, '0');
                    const yyyy = firstDay.getFullYear();
                    value = `${mm} ${dd} ${yyyy}`;
                    console.log(`[birFormFiller] Calculated FROM: ${value}`);
                } else {
                    console.warn(`[birFormFiller] Invalid voucher date format: ${voucherDateStr}`);
                    value = '';
                }
            } else {
                console.warn(`[birFormFiller] No voucher date found for dateRange.from`);
                value = '';
            }
        } else if (key === 'dateRange.to') {
            // Logic: End of the month of the Voucher Date
            const voucherDateStr = data.transaction?.voucher_date;
            console.log(`[birFormFiller] Processing dateRange.to. Voucher Date: "${voucherDateStr}"`);

            if (voucherDateStr) {
                const date = new Date(voucherDateStr);
                if (!isNaN(date.getTime())) {
                    // Set to last day of month (Month + 1, Day 0)
                    const lastDay = new Date(date.getFullYear(), date.getMonth() + 1, 0);
                    // Format: MM DD YYYY (e.g. 10 31 2025)
                    const mm = String(lastDay.getMonth() + 1).padStart(2, '0');
                    const dd = String(lastDay.getDate()).padStart(2, '0');
                    const yyyy = lastDay.getFullYear();
                    value = `${mm} ${dd} ${yyyy}`;
                    console.log(`[birFormFiller] Calculated TO: ${value}`);
                } else {
                    value = '';
                }
            } else {
                value = '';
            }
        } else {
            value = getNestedValue(data, key);
        }

        const fieldConfigs = asConfigArray(resolveConfig(key));
        
        // Debug Log for troubleshooting missing fields
        if (key === 'dateRange.from' || key === 'dateRange.to') {
            console.log(`[birFormFiller] Drawing ${key}: Value="${value}", Config=${JSON.stringify(fieldConfigs)}`);
        }
        
        // Draw if we have both a value and a valid config (coordinates)
        if (value !== undefined && value !== null && value !== '' && fieldConfigs.length > 0) {
            fieldConfigs.forEach((fieldConfig) => {
                if (!fieldConfig) return;

                if (key === 'payee.tin' || key === 'payor.tin') {
                    const strVal = String(value).replace(/[^0-9]/g, '');
                    const startX = fieldConfig.x;
                    const startY = fieldConfig.y;
                    const size = fieldConfig.size || 10;
                    const color = fieldConfig.color;

                    const charWidth = size * 0.6;
                    const spaceWidth = size * 0.3;

                    let currentX = startX;

                    for (let i = 0; i < strVal.length; i++) {
                        drawText(strVal[i], currentX, startY, size, color, fieldConfig);

                        let spaces = 1.5;

                        if (i === 2 || i === 5 || i === 8) {
                            spaces = 5.5;
                        } else if (i === 10) {
                            spaces = 2;
                        } else if (i === 12) {
                            spaces = 2;
                        } else if (i >= 11 && i <= 14) {
                            spaces = 3;
                        }

                        currentX += charWidth + (spaces * spaceWidth);
                    }
                    return;
                }

                if (key === 'payee.zip' || key === 'payor.zip') {
                    const strVal = String(value).replace(/\s/g, '');
                    const startX = fieldConfig.x;
                    const startY = fieldConfig.y;
                    const size = fieldConfig.size || 10;
                    const color = fieldConfig.color;

                    const charWidth = size * 0.6;
                    const spaceWidth = size * 0.3;
                    const gapSpaces = 1.5;

                    let currentX = startX;

                    for (let i = 0; i < strVal.length; i++) {
                        drawText(strVal[i], currentX, startY, size, color, fieldConfig);

                        if (i < strVal.length - 1) {
                            currentX += charWidth + (gapSpaces * spaceWidth);
                        }
                    }
                    return;
                }

                if (key === 'dateRange.from' || key === 'dateRange.to') {
                    const rawDate = String(value).replace(/\s/g, '');

                    const startX = fieldConfig.x;
                    const startY = fieldConfig.y;
                    const size = fieldConfig.size || 10;
                    const color = fieldConfig.color;

                    const charWidth = size * 0.6;
                    const spaceWidth = size * 0.3;

                    let currentX = startX;

                    for (let i = 0; i < rawDate.length; i++) {
                        drawText(rawDate[i], currentX, startY, size, color, fieldConfig);

                        let spaces = 1;

                        if (i === 0) spaces = 1;
                        else if (i === 1) spaces = 2;
                        else if (i === 2) spaces = 1.5;
                        else if (i === 3) spaces = 2;
                        else if (i === 4) spaces = 1.3;
                        else if (i === 5) spaces = 1.8;
                        else if (i === 6) spaces = 1.8;
                        else spaces = 0;

                        currentX += charWidth + (spaces * spaceWidth);
                    }
                    return;
                }

                // Default Draw with Alignment Support
                let finalX = fieldConfig.x;
                const align = fieldConfig.alignment || 'left';
                const boxWidth = fieldConfig.width || 0;
                const color = fieldConfig.color;
                const wrapText = fieldConfig.wrapText || false;
                
                if (!wrapText && align !== 'left' && boxWidth > 0) {
                    try {
                        const textWidth = font.widthOfTextAtSize(String(value), fieldConfig.size || 10);
                        if (align === 'center') {
                            finalX = fieldConfig.x + (boxWidth / 2) - (textWidth / 2);
                        } else if (align === 'right') {
                            finalX = fieldConfig.x + boxWidth - textWidth;
                        }
                    } catch (err) {
                        console.warn('Error calculating text width for alignment:', err);
                    }
                }

                drawText(value, finalX, fieldConfig.y, fieldConfig.size || 10, color, { ...fieldConfig, width: boxWidth, wrapText });
            });
        }
    });

    // Payments Table - Unified Handling
    const tableYConfig = mappingConfig?.['table.nature'];
    const customTableStartY = Array.isArray(tableYConfig) ? tableYConfig[0]?.y : tableYConfig?.y;

    const tableConfig = {
        nature: asConfigArray(mappingConfig?.['table.nature'] || (!templateUrl ? { x: config.colNature.x } : null)),
        atc: asConfigArray(mappingConfig?.['table.atc'] || (!templateUrl ? { x: config.colAtc.x } : null)),
        amount: asConfigArray(mappingConfig?.['table.amount'] || (!templateUrl ? { x: config.colAmount.x } : null)),
        tax: asConfigArray(mappingConfig?.['table.tax'] || (!templateUrl ? { x: config.colTax.x } : null)),
        startY: customTableStartY ?? config.tableStartY,
        rowHeight: config.rowHeight 
    };

    // Only draw table if we have at least one column configured
    if (tableConfig.nature.length > 0 || tableConfig.amount.length > 0) {
         let currentY = tableConfig.startY;
         
         payments.forEach((payment) => {
            const amountStr = parseFloat(payment.amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2 });
            const taxStr = parseFloat(payment.taxWithheld || 0).toLocaleString('en-US', { minimumFractionDigits: 2 });
            
            tableConfig.nature.forEach((cfg) => drawText(payment.description, cfg.x, currentY, cfg.size || 9, cfg.color, cfg));
            tableConfig.atc.forEach((cfg) => drawText(payment.atc, cfg.x, currentY, cfg.size || 9, cfg.color, cfg));
            tableConfig.amount.forEach((cfg) => drawText(amountStr, cfg.x, currentY, cfg.size || 9, cfg.color, cfg));
            tableConfig.tax.forEach((cfg) => drawText(taxStr, cfg.x, currentY, cfg.size || 9, cfg.color, cfg));
            
            currentY -= tableConfig.rowHeight;
         });
    }

    // 4. Serialize and Download
    const pdfBytes = await pdfDoc.save();
    return pdfBytes;

  } catch (error) {
    console.error('Error filling BIR form:', error);
    throw error;
  }
};
